import { TrashIcon, ExclamationTriangleIcon, EyeIcon, EyeSlashIcon } from '@heroicons/react/24/solid';
import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { apiUrl, authFetch, authService } from '../utils/api';
import { useUser } from '../contexts/UserContext';
import { P } from '../types/assistantPrivileges';

interface Subject {
  id: string;
  name: string;
  description: string;
  code: string;
  active: boolean;
  createdDate: string;
  modificationDate: string;
  gradeId: string;
  gradeName: string;
  teacherCount: number;
  courseCount: number;
}

interface Grade {
  id: string;
  name: string;
  description: string;
  level: number;
  code: string;
  roleName: string;
  active: boolean;
  createdDate: string;
  modificationDate: string;
  subjectCount: number;
}

interface FormData {
  name: string;
  description: string;
  code: string;
  gradeId: string;
  active: boolean;
}

const Subjects = () => {
  const navigate = useNavigate();
  const { hasPrivilege } = useUser();
  const canAddSubject = hasPrivilege(P.Subject_Add);
  const canEditSubject = hasPrivilege(P.Subject_Edit);
  const canDeleteSubject = hasPrivilege(P.Subject_Delete);
  const [subjects, setSubjects] = useState<Subject[]>([]);
  const [grades, setGrades] = useState<Grade[]>([]);
  const [selectedGradeId, setSelectedGradeId] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [subjectToDelete, setSubjectToDelete] = useState<Subject | null>(null);
  const [subjectToToggle, setSubjectToToggle] = useState<Subject | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [showAddForm, setShowAddForm] = useState(false);
  const [formData, setFormData] = useState<FormData>({
    name: '',
    description: '',
    code: '',
    gradeId: '',
    active: true,
  });

  // Fetch grades
  const fetchGrades = async () => {
    try {
      const response = await authFetch(apiUrl('Grade'), {
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${authService.getToken()}`
        }
      });
      
      const data = await response.json();
      
      if (data.success) {
        setGrades(data.data);
      } else {
        throw new Error(data.message || 'Failed to fetch grades');
      }
    } catch (error) {
      console.error('Error fetching grades:', error);
      setError('Failed to load grades. Please try again later.');
    }
  };

  // Fetch subjects
  const fetchSubjects = useCallback(async () => {
    setLoading(true);
    setError(null);
    
    try {
      const url = selectedGradeId 
        ? apiUrl(`Subject?gradeId=${selectedGradeId}`)
        : apiUrl('Subject');
        
      const response = await authFetch(url, {
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${authService.getToken()}`
        }
      });
      
      const data = await response.json();
      
      if (data.success) {
        setSubjects(data.data);
      } else {
        throw new Error(data.message || 'Failed to fetch subjects');
      }
    } catch (error) {
      console.error('Error fetching subjects:', error);
      setError('Failed to load subjects. Please try again later.');
      setSubjects([]);
    } finally {
      setLoading(false);
    }
  }, [selectedGradeId]);

  // Handle grade selection
  const handleGradeChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setSelectedGradeId(e.target.value);
  };

  // Handle subject deletion
  const handleDelete = async (id: string) => {
    setIsDeleting(true);
    
    try {
      const response = await authFetch(apiUrl(`Subject/${id}`), {
        method: 'DELETE',
        headers: {
          'accept': '*/*',
          'Authorization': `Bearer ${authService.getToken()}`
        }
      });
      
      const data = await response.json();
      
      if (data.success) {
        setSubjects(subjects.filter(subject => subject.id !== id));
        setSubjectToDelete(null);
      } else {
        throw new Error(data.message || 'Failed to delete subject');
      }
    } catch (error) {
      console.error('Error deleting subject:', error);
      alert('Failed to delete subject. Please try again later.');
    } finally {
      setIsDeleting(false);
    }
  };

  // Handle subject status toggle
  const handleToggleStatus = async (subject: Subject) => {
    try {
      const response = await authFetch(apiUrl(`Subject/${subject.id}`), {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Accept': '*/*',
          'Authorization': `Bearer ${authService.getToken()}`
        },
        body: JSON.stringify({
          id: subject.id,
          name: subject.name,
          description: subject.description,
          code: subject.code,
          gradeId: subject.gradeId,
          active: !subject.active
        })
      });
      
      const data = await response.json();
      
      if (data.success) {
        setSubjects(
          subjects.map(s =>
            s.id === subject.id
              ? { ...s, active: !s.active }
              : s
          )
        );
        setSubjectToToggle(null);
      } else {
        throw new Error(data.message || 'Failed to update subject status');
      }
    } catch (error) {
      console.error('Error updating subject status:', error);
      alert('Failed to update subject status. Please try again later.');
    }
  };

  // Handle form input changes
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value, type } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? (e.target as HTMLInputElement).checked : value
    }));
  };

  // Handle form submission
  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    
    try {
      // Validate required fields
      if (!formData.name || !formData.description || !formData.code || !formData.gradeId) {
        alert('يرجى ملء جميع الحقول المطلوبة');
        return;
      }

      const response = await authFetch(apiUrl('Subject'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${authService.getToken()}`
        },
        body: JSON.stringify({
          name: formData.name,
          description: formData.description,
          code: formData.code,
          gradeId: formData.gradeId,
          active: formData.active
        })
      });
      
      const data = await response.json();
      
      if (data.success) {
        // Refresh the subjects list after successful addition
        setShowAddForm(false);
        setFormData({
          name: '',
          description: '',
          code: '',
          gradeId: '',
          active: true,
        });
        fetchSubjects();
      } else {
        alert('Failed to add subject: ' + data.message);
      }
    } catch (error) {
      console.error('Error adding subject:', error);
      alert('Failed to add subject. Please try again later.');
    }
  };

  // Initial fetch of grades and subjects
  useEffect(() => {
    fetchGrades();
  }, []);

  // Fetch subjects when grade selection changes
  useEffect(() => {
    fetchSubjects();
  }, [fetchSubjects]);

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100" dir="rtl">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="mb-8">
          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
            <div>
              <h1 className="text-4xl font-extrabold text-transparent bg-clip-text bg-gradient-to-r from-blue-600 to-indigo-600">
                المواد الدراسية
              </h1>
              <p className="mt-2 text-gray-600">إدارة المواد الدراسية</p>
            </div>
            {canAddSubject && (
            <button
              onClick={() => setShowAddForm(true)}
              className="inline-flex items-center px-4 py-2 bg-gradient-to-r from-blue-600 to-indigo-600 text-white text-sm rounded-lg 
                         shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 transition-all duration-200 focus:outline-none focus:ring-2 
                         focus:ring-offset-2 focus:ring-blue-500"
            >
              <svg className="w-5 h-5 ml-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
              </svg>
              إضافة مادة جديدة
            </button>
            )}
          </div>
        </div>

        {/* Filters */}
        <div className="bg-white rounded-2xl shadow-lg p-6 mb-8 transition-all duration-200 hover:shadow-xl">
          <div className="grid grid-cols-1 gap-6">
            <div className="space-y-1">
              <label htmlFor="grade-filter" className="block text-sm font-medium text-gray-700">
                السنة الدراسية
              </label>
              <select
                id="grade-filter"
                value={selectedGradeId}
                onChange={handleGradeChange}
                className="block w-full px-4 py-2 rounded-lg border border-gray-200 focus:ring-2 focus:ring-blue-500 focus:border-transparent
                         transition-colors duration-200"
              >
                <option value="">جميع السنوات الدراسية</option>
                {grades.map((grade) => (
                  <option key={grade.id} value={grade.id}>
                    {grade.name}
                  </option>
                ))}
              </select>
            </div>
          </div>
        </div>

        {/* Subjects Table */}
        <div className="bg-white rounded-2xl shadow-lg overflow-hidden transition-all duration-200 hover:shadow-xl">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead>
                <tr className="bg-gradient-to-r from-gray-50 to-gray-100">
                  <th scope="col" className="px-6 py-4 text-right text-xs font-semibold text-gray-600 uppercase tracking-wider">
                    اسم المادة
                  </th>
                  <th scope="col" className="px-6 py-4 text-right text-xs font-semibold text-gray-600 uppercase tracking-wider">
                    الوصف
                  </th>
                  <th scope="col" className="px-6 py-4 text-right text-xs font-semibold text-gray-600 uppercase tracking-wider">
                    السنة الدراسية
                  </th>
                  <th scope="col" className="px-6 py-4 text-right text-xs font-semibold text-gray-600 uppercase tracking-wider">
                    عدد المعلمين
                  </th>
                  <th scope="col" className="px-6 py-4 text-right text-xs font-semibold text-gray-600 uppercase tracking-wider">
                    عدد الدورات
                  </th>
                  <th scope="col" className="px-6 py-4 text-right text-xs font-semibold text-gray-600 uppercase tracking-wider">
                    الإجراءات
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {loading ? (
                  <tr>
                    <td colSpan={6} className="px-6 py-12">
                      <div className="flex justify-center">
                        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                      </div>
                    </td>
                  </tr>
                ) : error ? (
                  <tr>
                    <td colSpan={6} className="px-6 py-4">
                      <div className="flex items-center justify-center text-red-500">
                        <ExclamationTriangleIcon className="w-5 h-5 ml-2" />
                        {error}
                      </div>
                    </td>
                  </tr>
                ) : subjects.length === 0 ? (
                  <tr>
                    <td colSpan={6} className="px-6 py-12 text-center">
                      <div className="flex flex-col items-center justify-center text-gray-500">
                        <svg className="w-12 h-12 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
                        </svg>
                        <p className="text-lg font-medium">لم يتم العثور على مواد</p>
                        <p className="text-sm">حاول تعديل التصفية للعثور على ما تبحث عنه</p>
                      </div>
                    </td>
                  </tr>
                ) : (
                  subjects.map((subject) => (
                    <tr 
                      key={subject.id} 
                      className="hover:bg-gray-50 cursor-pointer"
                      onClick={() => navigate(`/subjects/${subject.id}`)}
                    >
                      <td className="px-6 py-4">
                        <div className="text-sm font-medium text-gray-900">{subject.name}</div>
                      </td>
                      <td className="px-6 py-4">
                        <div className="text-sm text-gray-500">{subject.description}</div>
                      </td>
                      <td className="px-6 py-4">
                        <div className="text-sm text-gray-900">{subject.gradeName}</div>
                      </td>
                      <td className="px-6 py-4">
                        <div className="text-sm text-gray-900">{subject.teacherCount}</div>
                      </td>
                      <td className="px-6 py-4">
                        <div className="text-sm text-gray-900">{subject.courseCount}</div>
                      </td>
                      <td className="px-6 py-4 text-right">
                        <div className="flex items-center justify-end space-x-reverse space-x-2">
                          {canEditSubject && (
                          <button
                            onClick={() => setSubjectToToggle(subject)}
                            className={`inline-flex items-center p-2 rounded-lg transition-colors duration-200 ${
                              subject.active
                                ? 'text-yellow-600 hover:text-yellow-900 hover:bg-yellow-50'
                                : 'text-green-600 hover:text-green-900 hover:bg-green-50'
                            }`}
                            title={subject.active ? 'إخفاء المادة' : 'إظهار المادة'}
                          >
                            {subject.active ? (
                              <EyeSlashIcon className="h-5 w-5" />
                            ) : (
                              <EyeIcon className="h-5 w-5" />
                            )}
                          </button>
                          )}
                          {canDeleteSubject && (
                          <button
                            onClick={() => setSubjectToDelete(subject)}
                            className="inline-flex items-center p-2 text-red-600 hover:text-red-900 rounded-lg hover:bg-red-50
                                     transition-colors duration-200"
                            title="حذف المادة"
                          >
                            <TrashIcon className="h-5 w-5" />
                          </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>

        {/* Delete Confirmation Modal */}
        {subjectToDelete && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-40 backdrop-blur-sm">
            <div className="bg-white rounded-xl shadow-2xl p-6 w-full max-w-md border border-gray-200 transform transition-all">
              <div className="text-center mb-5">
                <div className="mx-auto flex items-center justify-center h-16 w-16 rounded-full bg-red-100 mb-4">
                  <TrashIcon className="h-8 w-8 text-red-600" aria-hidden="true" />
                </div>
                <h3 className="text-xl font-medium text-gray-900 mb-2">حذف المادة</h3>
                <p className="text-gray-600">
                  هل أنت متأكد من حذف <span className="font-bold text-gray-900">{subjectToDelete.name}</span>؟
                  لا يمكن التراجع عن هذا الإجراء.
                </p>
              </div>
              <div className="mt-6 flex justify-end gap-3">
                <button
                  className="px-4 py-2 rounded-lg bg-gray-100 text-gray-700 font-medium hover:bg-gray-200 transition-all focus:outline-none focus:ring-2 focus:ring-gray-300"
                  onClick={() => setSubjectToDelete(null)}
                  disabled={isDeleting}
                >
                  إلغاء
                </button>
                <button
                  className="px-4 py-2 rounded-lg bg-red-600 text-white font-medium hover:bg-red-700 transition-all focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2 disabled:opacity-70 flex items-center gap-2"
                  onClick={() => handleDelete(subjectToDelete.id)}
                  disabled={isDeleting}
                >
                  {isDeleting ? (
                    <>
                      <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      جاري الحذف...
                    </>
                  ) : (
                    <>
                      <TrashIcon className="h-4 w-4" />
                      حذف
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Status Change Confirmation Modal */}
        {subjectToToggle && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-40 backdrop-blur-sm">
            <div className="bg-white rounded-xl shadow-2xl p-6 w-full max-w-md border border-gray-200 transform transition-all">
              <div className="text-center mb-5">
                <div className="mx-auto flex items-center justify-center h-16 w-16 rounded-full bg-blue-100 mb-4">
                  <ExclamationTriangleIcon className="h-8 w-8 text-blue-600" aria-hidden="true" />
                </div>
                <h3 className="text-xl font-medium text-gray-900 mb-2">
                  {subjectToToggle.active ? 'إخفاء المادة' : 'إظهار المادة'}
                </h3>
                <p className="text-gray-600">
                  هل أنت متأكد من أنك تريد {subjectToToggle.active ? 'إخفاء' : 'إظهار'} المادة <span className="font-bold text-gray-900">{subjectToToggle.name}</span>؟
                </p>
              </div>
              <div className="mt-6 flex justify-end gap-3">
                <button
                  className="px-4 py-2 rounded-lg bg-gray-100 text-gray-700 font-medium hover:bg-gray-200 transition-all focus:outline-none focus:ring-2 focus:ring-gray-300"
                  onClick={() => setSubjectToToggle(null)}
                >
                  إلغاء
                </button>
                <button
                  className={`px-4 py-2 rounded-lg ${
                    subjectToToggle.active
                      ? 'bg-yellow-600 hover:bg-yellow-700'
                      : 'bg-green-600 hover:bg-green-700'
                  } text-white font-medium transition-all focus:outline-none focus:ring-2 focus:ring-offset-2 ${
                    subjectToToggle.active
                      ? 'focus:ring-yellow-500'
                      : 'focus:ring-green-500'
                  }`}
                  onClick={() => handleToggleStatus(subjectToToggle)}
                >
                  {subjectToToggle.active ? 'إخفاء المادة' : 'إظهار المادة'}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Add Subject Form Modal */}
        {showAddForm && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
            <div className="bg-white rounded-lg p-6 max-w-md w-full max-h-[90vh] overflow-y-auto">
              <h2 className="text-xl sm:text-2xl font-bold mb-6">إضافة مادة جديدة</h2>
              <form onSubmit={handleSubmit}>
                <div className="space-y-4">
                  <div>
                    <label className="block text-gray-700 text-sm font-bold mb-2">
                      اسم المادة
                    </label>
                    <input
                      type="text"
                      value={formData.name}
                      onChange={handleInputChange}
                      name="name"
                      className="w-full px-3 py-2 border rounded-lg"
                      required
                      placeholder="أدخل اسم المادة"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-gray-700 text-sm font-bold mb-2">
                      وصف المادة
                    </label>
                    <textarea
                      value={formData.description}
                      onChange={handleInputChange}
                      name="description"
                      className="w-full px-3 py-2 border rounded-lg"
                      rows={3}
                      required
                      placeholder="أدخل وصف المادة"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-gray-700 text-sm font-bold mb-2">
                      كود المادة
                    </label>
                    <input
                      type="text"
                      value={formData.code}
                      onChange={handleInputChange}
                      name="code"
                      className="w-full px-3 py-2 border rounded-lg"
                      required
                      placeholder="أدخل كود المادة"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-gray-700 text-sm font-bold mb-2">
                      السنة الدراسية
                    </label>
                    <select
                      value={formData.gradeId}
                      onChange={handleInputChange}
                      name="gradeId"
                      className="w-full px-3 py-2 border rounded-lg"
                      required
                    >
                      <option value="">اختر السنة الدراسية</option>
                      {grades.map((grade) => (
                        <option key={grade.id} value={grade.id}>
                          {grade.name}
                        </option>
                      ))}
                    </select>
                  </div>
                  
                  <div>
                    <label className="block text-gray-700 text-sm font-bold mb-2">
                      الحالة
                    </label>
                    <select
                      value={formData.active ? 'true' : 'false'}
                      onChange={handleInputChange}
                      name="active"
                      className="w-full px-3 py-2 border rounded-lg"
                    >
                      <option value="true">نشط</option>
                      <option value="false">غير نشط</option>
                    </select>
                  </div>
                </div>
                <div className="flex justify-end gap-4 mt-6">
                  <button
                    type="button"
                    onClick={() => setShowAddForm(false)}
                    className="px-4 py-2 text-gray-600 hover:text-gray-800"
                  >
                    إلغاء
                  </button>
                  <button
                    type="submit"
                    className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                  >
                    إضافة المادة
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default Subjects;

